import React, { useMemo, useCallback, useState } from 'react';
import { JSONTree } from 'react-json-tree';
import { ChevronDown, ChevronRight, Copy, Check, Code, TreeDeciduous } from 'lucide-react';
import { palette, typography, withOpacity } from '../utils/designTokens';
import { isMalformedJson } from '../utils/mimeType';

/**
 * JSONTreeViewer - Interactive JSON/Dict tree with collapsible nodes
 *
 * Built on react-json-tree with Lumoz design system theming.
 *
 * Features:
 * - Collapsible parent/child navigation
 * - Syntax highlighting matching design tokens
 * - Expand all / Collapse all controls
 * - Copy value on click
 * - Renders a value it cannot parse as plain text, without calling it an error
 * - Auto-detects and converts Python dict syntax
 *
 * A string that is not JSON is not a defect — plain text is a perfectly valid span value, and
 * this component used to report it as "Invalid JSON" in red on nearly every trace. The only
 * genuinely broken case is a span that DECLARES application/json and then hands over something
 * unparseable; pass `mimeType` to surface that one.
 *
 * @param {Object|string} data - JSON, Python dict, or plain text to display
 * @param {string} mimeType - Optional span mime type. Only when this declares JSON is a parse
 *                            failure treated as a problem worth showing the user.
 * @param {string} label - Optional root label
 * @param {number} initialExpandDepth - How many levels to expand initially (default: 0, collapsed)
 * @param {string} maxHeight - Max height with scroll (default: '400px')
 * @param {boolean} showControls - Show expand/collapse all buttons (default: true)
 * @param {boolean} showFormatBadge - Show format badge (JSON/Dict) (default: true)
 */
const JSONTreeViewer = ({
  data,
  mimeType = null,
  label,
  initialExpandDepth = 0,
  maxHeight = '400px',
  showControls = true,
  showFormatBadge = true,
  truncated = false,
  className = ''
}) => {
  // Track expansion state: null = use initialExpandDepth, true = all expanded, false = all collapsed
  const [expandState, setExpandState] = useState(initialExpandDepth > 0 ? 'partial' : 'collapsed');
  const [treeKey, setTreeKey] = useState(0);
  const [copiedPath, setCopiedPath] = useState(null);
  // View mode: 'tree' or 'raw'
  const [viewMode, setViewMode] = useState('tree');

  // Handle expand all
  const handleExpandAll = () => {
    setExpandState('expanded');
    setTreeKey(k => k + 1);
  };

  // Handle collapse all
  const handleCollapseAll = () => {
    setExpandState('collapsed');
    setTreeKey(k => k + 1);
  };

  // Determine if nodes should be expanded
  const getExpandAll = () => {
    if (expandState === 'expanded') return true;
    if (expandState === 'collapsed') return false;
    return null; // Use initialExpandDepth
  };

  // Detect if string looks like Python dict syntax (single quotes)
  const isPythonDict = useMemo(() => {
    if (typeof data !== 'string') return false;
    // Check if it starts with { and contains single-quoted keys like {'key':
    return /^\s*\{.*'[^']+'\s*:/.test(data);
  }, [data]);

  // Parse data if it's a string
  const parsedData = useMemo(() => {
    if (!data) return null;
    if (typeof data === 'string') {
      // First try standard JSON parsing
      try {
        return JSON.parse(data);
      } catch {
        // Try to convert Python dict syntax to JSON for tree viewing
        try {
          const jsonified = data
            // Replace Python None/True/False with JSON equivalents
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false')
            // Replace single quotes with double quotes
            .replace(/'/g, '"');
          return JSON.parse(jsonified);
        } catch {
          return { _parseError: true, _raw: data };
        }
      }
    }
    return data;
  }, [data]);

  // Get the raw string for raw view mode (pretty-printed)
  const rawString = useMemo(() => {
    if (typeof data === 'string') {
      // Try to parse and re-stringify for pretty formatting
      try {
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      } catch {
        // If it's Python dict syntax, try converting first
        try {
          const jsonified = data
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false')
            .replace(/'/g, '"');
          const parsed = JSON.parse(jsonified);
          return JSON.stringify(parsed, null, 2);
        } catch {
          // Return as-is if parsing fails
          return data;
        }
      }
    }
    // If data is already an object, stringify it nicely
    return JSON.stringify(data, null, 2);
  }, [data]);

  // Theme matching Lumoz design tokens
  const theme = useMemo(() => ({
    scheme: 'lumoz',
    author: 'lumoz',
    base00: palette.neutrals.canvas,           // Background
    base01: palette.neutrals.panelPrimary,     // Lighter background
    base02: palette.neutrals.panelSecondary,   // Selection background
    base03: palette.text.muted,                // Comments, invisibles
    base04: palette.text.secondary,            // Dark foreground
    base05: palette.text.primary,              // Default foreground
    base06: palette.text.primary,              // Light foreground
    base07: palette.text.heading,              // Light background
    base08: palette.status.danger,             // Variables, errors
    base09: palette.accents.warm.base,         // Integers, booleans, constants
    base0A: palette.accents.warm.base,         // Classes, search highlight
    base0B: palette.accents.primary.base,      // Strings - Pastel Teal
    base0C: palette.accents.secondary.base,    // Support, regex - Soft Periwinkle
    base0D: palette.support.attributeLabel,    // Functions, methods - Warm Beige (keys)
    base0E: palette.accents.secondary.base,    // Keywords - Soft Periwinkle
    base0F: palette.status.danger,             // Deprecated
  }), []);

  // Determine if a keyPath should be expanded
  const shouldExpandNode = useCallback((keyPath, data, level) => {
    const expand = getExpandAll();
    if (expand !== null) return expand;
    return level < initialExpandDepth;
  }, [expandState, initialExpandDepth]);

  // Copy value to clipboard
  const handleValueClick = useCallback((value, keyPath) => {
    const textToCopy = typeof value === 'object'
      ? JSON.stringify(value, null, 2)
      : String(value);

    navigator.clipboard.writeText(textToCopy).then(() => {
      const pathKey = keyPath.join('.');
      setCopiedPath(pathKey);
      setTimeout(() => setCopiedPath(null), 1500);
    });
  }, []);

  // Custom label renderer for better key styling
  const labelRenderer = useCallback((keyPath, nodeType, expanded, expandable) => {
    const key = keyPath[0];
    const isArrayIndex = typeof key === 'number';

    return (
      <span
        style={{
          color: isArrayIndex ? palette.accents.secondary.base : palette.support.attributeLabel,
          fontWeight: isArrayIndex ? 400 : 500,
        }}
      >
        {isArrayIndex ? `[${key}]` : key}
        <span style={{ color: palette.text.muted }}>: </span>
      </span>
    );
  }, []);

  // Custom value renderer with copy functionality
  const valueRenderer = useCallback((valueAsString, value, ...keyPath) => {
    const pathKey = keyPath.join('.');
    const isCopied = copiedPath === pathKey;

    return (
      <span
        className="group cursor-pointer inline-flex items-center gap-1"
        onClick={() => handleValueClick(value, keyPath)}
        title="Click to copy"
      >
        <span>{valueAsString}</span>
        {isCopied ? (
          <Check className="w-3 h-3 text-green-400" />
        ) : (
          <Copy className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
        )}
      </span>
    );
  }, [copiedPath, handleValueClick]);

  // Handle invalid/empty data
  if (!parsedData) {
    return null;
  }

  // Could not parse. Plain text is a valid value, so this is only a PROBLEM when the span said
  // the value would be JSON. Otherwise render the text plainly, with no notice and no red border.
  if (parsedData._parseError) {
    // Never flag a truncated preview: valid JSON cut mid-string always fails to parse, and
    // that is truncation, not an instrumentation defect.
    const declaredJsonButUnparseable = !truncated && isMalformedJson(mimeType, data);
    const notice = truncated
      ? 'Content truncated for display — showing the first part of the raw value'
      : (declaredJsonButUnparseable
          ? 'This span declared its value as application/json, but it could not be parsed. Showing the raw value.'
          : null);

    return (
      <div className={className}>
        {label && (
          <div
            className="font-medium mb-2"
            style={{ fontSize: typography.size.sm, color: palette.text.secondary }}
          >
            {label}
          </div>
        )}
        <div
          className="p-3 rounded border font-mono overflow-auto"
          style={{
            fontSize: '12px',
            backgroundColor: palette.neutrals.canvas,
            borderColor: declaredJsonButUnparseable ? palette.status.danger : palette.borders.subtle,
            maxHeight,
          }}
        >
          {notice && (
            <div
              className="mb-2"
              style={{ color: declaredJsonButUnparseable ? palette.status.danger : palette.text.secondary }}
            >
              {notice}
            </div>
          )}
          <pre style={{ color: palette.support.codeText, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {parsedData._raw}
          </pre>
        </div>
      </div>
    );
  }

  // Determine format label
  const formatLabel = isPythonDict ? 'Dict' : 'JSON';

  return (
    <div className={className}>
      {/* Header with label, format badge, and controls */}
      {(label || showControls || showFormatBadge) && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {label && (
              <div
                className="font-medium"
                style={{ fontSize: typography.size.sm, color: palette.text.secondary }}
              >
                {label}
              </div>
            )}
            {showFormatBadge && (
              <span
                className="px-1.5 py-0.5 rounded text-xs font-mono"
                style={{
                  backgroundColor: isPythonDict ? withOpacity(palette.accents.warm.base, 13) : withOpacity(palette.accents.primary.base, 13),
                  color: isPythonDict ? palette.accents.warm.base : palette.accents.primary.base,
                }}
                title={isPythonDict ? 'Python dictionary syntax (converted for display)' : 'JSON format'}
              >
                {formatLabel}
              </span>
            )}
          </div>
          {/* View mode toggle */}
          <div
            className="flex items-center rounded overflow-hidden border"
            style={{ borderColor: palette.borders.subtle }}
          >
            <button
              onClick={() => setViewMode('tree')}
              className="flex items-center gap-1 px-2 py-0.5 text-xs transition-colors"
              style={{
                backgroundColor: viewMode === 'tree' ? withOpacity(palette.accents.primary.base, 19) : 'transparent',
                color: viewMode === 'tree' ? palette.accents.primary.base : palette.text.muted,
              }}
              title="Formatted view"
            >
              <TreeDeciduous className="w-3 h-3" />
              <span>Formatted</span>
            </button>
            <button
              onClick={() => setViewMode('raw')}
              className="flex items-center gap-1 px-2 py-0.5 text-xs transition-colors"
              style={{
                backgroundColor: viewMode === 'raw' ? withOpacity(palette.accents.primary.base, 19) : 'transparent',
                color: viewMode === 'raw' ? palette.accents.primary.base : palette.text.muted,
              }}
              title="Raw string view"
            >
              <Code className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Content area - Tree or Raw view */}
      <div
        className={`p-3 rounded border overflow-auto ${viewMode === 'tree' ? 'json-tree-wrapper' : ''}`}
        style={{
          backgroundColor: palette.neutrals.canvas,
          borderColor: palette.borders.subtle,
          maxHeight,
        }}
      >
        {viewMode === 'raw' ? (
          /* Raw string view */
          <pre
            className="font-mono text-xs"
            style={{
              color: palette.support.codeText,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
            }}
          >
            {rawString}
          </pre>
        ) : (
          /* Tree view */
          <>
            {/* Expand/Collapse controls inside tree view - sticky at top */}
            {showControls && (
              <div
                className="flex items-center gap-1 pb-2 mb-2 border-b"
                style={{
                  borderColor: palette.borders.subtle,
                  position: 'sticky',
                  top: '-12px',
                  backgroundColor: palette.neutrals.canvas,
                  zIndex: 10,
                  marginLeft: '-12px',
                  marginRight: '-12px',
                  marginTop: '-12px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  paddingTop: '12px',
                }}
              >
                <button
                  onClick={handleExpandAll}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors hover:bg-white/10"
                  style={{ color: palette.text.muted }}
                  title="Expand all nodes"
                >
                  <ChevronDown className="w-3 h-3" />
                  <span>Expand All</span>
                </button>
                <button
                  onClick={handleCollapseAll}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors hover:bg-white/10"
                  style={{ color: palette.text.muted }}
                  title="Collapse all nodes"
                >
                  <ChevronRight className="w-3 h-3" />
                  <span>Collapse All</span>
                </button>
              </div>
            )}
            <style>{`
              .json-tree-wrapper > ul {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
              }
              .json-tree-wrapper ul {
                word-break: break-word !important;
                overflow-wrap: anywhere !important;
                white-space: pre-wrap !important;
                max-width: 100% !important;
              }
              .json-tree-wrapper li {
                word-break: break-word !important;
                overflow-wrap: anywhere !important;
                max-width: 100% !important;
                display: block !important;
              }
              .json-tree-wrapper li > span,
              .json-tree-wrapper li > label {
                white-space: pre-wrap !important;
                word-break: break-word !important;
                display: inline !important;
              }
            `}</style>
            <JSONTree
              key={treeKey}
              data={parsedData}
              theme={theme}
              invertTheme={false}
              hideRoot={!label}
              shouldExpandNodeInitially={shouldExpandNode}
              labelRenderer={labelRenderer}
              valueRenderer={valueRenderer}
              getItemString={(type, data, itemType, itemString) => {
                // Show count for arrays/objects
                if (type === 'Array') {
                  return <span style={{ color: palette.text.muted }}>{`[${data.length}]`}</span>;
                }
                if (type === 'Object') {
                  const keys = Object.keys(data).length;
                  return <span style={{ color: palette.text.muted }}>{`{${keys}}`}</span>;
                }
                return null;
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default JSONTreeViewer;
